import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';

// Material-UI Core Components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';

// Material-UI Hooks for Responsiveness
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// --- Tabler Icons ---
import {
  IconLanguage,
  IconCurrencyDollar,
  IconCompass,
  IconHeadset,
  IconMail,
  IconPhone,
  IconBrandWhatsapp,
  IconBrandTripadvisor,
  IconBrandGoogle,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandPinterest,
  IconBrandYoutube,
} from '@tabler/icons-react';

import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';

// --- Data for Menus with Country Codes ---
const languages = [
  { code: 'en', name: 'English', country_code: 'US' },
  { code: 'de', name: 'Deutsch (German)', country_code: 'DE' },
  { code: 'fr', name: 'Français (French)', country_code: 'FR' },
  { code: 'es', name: 'Español (Spanish)', country_code: 'ES' },
  { code: 'it', name: 'Italiano (Italian)', country_code: 'IT' },
  { code: 'zh', name: '中文 (Chinese)', country_code: 'CN' },
  { code: 'ja', name: '日本語 (Japanese)', country_code: 'JP' },
  { code: 'ru', name: 'Русский (Russian)', country_code: 'RU' },
  { code: 'ar', name: 'العربية (Arabic)', country_code: 'SA' },
  { code: 'hi', name: 'हिन्दी (Hindi)', country_code: 'IN' },
];

const currencies = [
  { code: 'USD', name: 'USD - US Dollar', country_code: 'US' },
  { code: 'EUR', name: 'EUR - Euro', country_code: 'EU' },
  { code: 'GBP', name: 'GBP - British Pound', country_code: 'GB' },
  { code: 'INR', name: 'INR - Indian Rupee', country_code: 'IN' },
  { code: 'AUD', name: 'AUD - Australian Dollar', country_code: 'AU' },
  { code: 'CAD', name: 'CAD - Canadian Dollar', country_code: 'CA' },
  { code: 'CNY', name: 'CNY - Chinese Yuan', country_code: 'CN' },
  { code: 'JPY', name: 'JPY - Japanese Yen', country_code: 'JP' },
  { code: 'RUB', name: 'RUB - Russian Ruble', country_code: 'RU' },
  { code: 'CHF', name: 'CHF - Swiss Franc', country_code: 'CH' },
  { code: 'AED', name: 'AED - UAE Dirham', country_code: 'AE' },
];

// --- Component ---

const OrientationBar = () => {
  const { t } = useTranslation();

  const { setLanguage } = useLanguage();

  // --- State Management for Menus ---
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [currencyAnchorEl, setCurrencyAnchorEl] = useState(null);

  // --- Responsiveness Hooks ---
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { setCurrency } = useCurrency();

  // --- Generic Menu Handlers ---
  const handleMenuOpen = (setter) => (event) => setter(event.currentTarget);
  const handleMenuClose = (setter) => () => setter(null);

  // --- Language Change Handler ---
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleMenuClose(setLanguageAnchorEl)();
  };

  const handleCurrencyChange = (curr) => {
    setCurrency(curr);
    handleMenuClose(setCurrencyAnchorEl)();
  };

  // --- Style Definitions ---
  const textAndIconColor = '#2E3836';
  const accentColor = '#D4A259';

  const commonTextStyle = {
    fontFamily: "'Lato', sans-serif",
    color: textAndIconColor,
    fontSize: '0.8rem',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  };

  const tablerIconProps = {
    size: 20,
    stroke: 1.5,
    style: { color: textAndIconColor, flexShrink: 0 },
    className: 'tabler-icon',
  };

  const iconContainerStyle = {
    '&:hover .tabler-icon': { color: accentColor },
    '&:hover': { color: accentColor },
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: '#FCF9F3',
        height: '40px',
        minHeight: '40px',
        justifyContent: 'center',
        borderBottom: '1px solid #00000046',
      }}
    >
      <Toolbar variant="dense" sx={{ position: 'relative', minHeight: '40px' }}>
        {/* Central Compass Icon */}
        {!isSmallScreen && (
          <IconCompass
            size={24}
            stroke={1.5}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'rgba(46, 56, 54, 0.66)',
            }}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Left Side: User Orientation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1.5 } }}>
            {/* Language Menu */}
            <IconButton size="small" sx={iconContainerStyle} onClick={handleMenuOpen(setLanguageAnchorEl)}>
              <IconLanguage {...tablerIconProps} />
            </IconButton>
            <Menu anchorEl={languageAnchorEl} open={Boolean(languageAnchorEl)} onClose={handleMenuClose(setLanguageAnchorEl)}>
              {languages.map((lang) => (
                <MenuItem key={lang.code} onClick={() => handleLanguageChange(lang.code)}>
                  <ReactCountryFlag
                    countryCode={lang.country_code}
                    svg
                    style={{
                      width: '1.5em',
                      height: '1.5em',
                      marginRight: '10px',
                    }}
                    title={lang.country_code}
                  />
                  {lang.name}
                </MenuItem>
              ))}
            </Menu>

            {/* Currency Menu */}
            <IconButton size="small" sx={iconContainerStyle} onClick={handleMenuOpen(setCurrencyAnchorEl)}>
              <IconCurrencyDollar {...tablerIconProps} />
            </IconButton>
            <Menu anchorEl={currencyAnchorEl} open={Boolean(currencyAnchorEl)} onClose={handleMenuClose(setCurrencyAnchorEl)}>
               {currencies.map((curr) => (
                <MenuItem key={curr.code} onClick={() => handleCurrencyChange(curr.code)}>
                  <ReactCountryFlag
                    countryCode={curr.country_code}
                    svg
                    style={{
                      width: '1.5em',
                      height: '1.5em',
                      marginRight: '10px',
                    }}
                    title={curr.country_code}
                  />
                  {curr.name}
                </MenuItem>
              ))}
            </Menu>

            {/* Contact Links - Text hidden on smaller screens */}
            <Link href="mailto:concierge@bellaceyon.com" sx={{ ...commonTextStyle, ...iconContainerStyle, display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}>
              <IconMail {...tablerIconProps} size={18} />
              {isLargeScreen && t('email_us')}
            </Link>
            <Link href="https://wa.me/94777272723" target="_blank" rel="noopener noreferrer" sx={{ ...commonTextStyle, ...iconContainerStyle, display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}>
              <IconBrandWhatsapp {...tablerIconProps} size={18} />
              {isLargeScreen && t('whatsapp')}
            </Link>
            <Link href="/contact-us" sx={{ ...commonTextStyle, ...iconContainerStyle, display: 'flex', alignItems: 'center', gap: 0.5,  textDecoration: 'none' }}>
              <IconPhone {...tablerIconProps} size={18} />
              {isLargeScreen && t('contact_us')}
            </Link>
            <Link href="/talk-to-expert" sx={{ ...commonTextStyle, ...iconContainerStyle, display: 'flex', alignItems: 'center', gap: 0.5,  textDecoration: 'none' }}>
              <IconHeadset {...tablerIconProps} size={18} />
              {isLargeScreen && t('talk_to_expert')}
            </Link>
          </Box>

          {/* Right Side: Brand Orientation & Trust */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            {/* Trust Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isLargeScreen && <Typography sx={commonTextStyle}>{t('trusted_by_travelers')}</Typography>}
              <Link href="#" target="_blank" rel="noopener noreferrer" sx={iconContainerStyle}><IconBrandTripadvisor {...tablerIconProps} /></Link>
              <Link href="#" target="_blank" rel="noopener noreferrer" sx={iconContainerStyle}><IconBrandGoogle {...tablerIconProps} /></Link>
            </Box>

            {/* Social Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isLargeScreen && <Typography sx={commonTextStyle}>{t('follow_our_journey')}</Typography>}
              <Link href="#" target="_blank" rel="noopener noreferrer" sx={iconContainerStyle}><IconBrandFacebook {...tablerIconProps} /></Link>
              <Link href="#" target="_blank" rel="noopener noreferrer" sx={iconContainerStyle}><IconBrandInstagram {...tablerIconProps} /></Link>
              <Link href="#" target="_blank" rel="noopener noreferrer" sx={iconContainerStyle}><IconBrandPinterest {...tablerIconProps} /></Link>
              <Link href="#" target="_blank" rel="noopener noreferrer" sx={iconContainerStyle}><IconBrandYoutube {...tablerIconProps} /></Link>
            </Box>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default OrientationBar;